<script setup>

    import { computed } from 'vue'
    import { util, handleBlur } from '@/Helpers'
    import moment from 'moment'
    import { storeToRefs } from 'pinia'
    import ClientCaregiverScheduleViewBlock from '@/Components/Caregiver/ClientCaregiverScheduleViewBlock.vue'
    import { useCaregiverSingleStore } from '@/Stores'

    const caregiverSingleStore = useCaregiverSingleStore()
    const { userFocusedOn } = storeToRefs(caregiverSingleStore)

    const props = defineProps({
        schedule_start_date: {
            type: String,
            required: true
        },
        block: {
            type: Object,
            required: true
        },
        displayMode: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'search', 'search_verbose'].includes(value)
        },
        showLabel: {
            type: Boolean,
            default: true
        }
    })

    const isCurrentOrFutureWeek = computed(() => {
        const lastSunday = moment(util.date.lastSunday(), util.date.date_format)
        const startDate = moment(props.schedule_start_date, util.date.date_format)

        return startDate.isSameOrAfter(lastSunday);
    })

    function popperTheme() {
        if (isCurrentOrFutureWeek.value) {
            return 'lifeworx-schedule-blue';
        } else {
            return 'lifeworx-schedule-gray';
        }
    }

</script>


<template>
    <li
        class="relative flex group ml-1.5 mr-3.5"
        :class="[
            `col-start-${util.date.dateDiff(schedule_start_date, block.start_date) + 1}`,
            {
                'z-40': userFocusedOn == 'client_shift' && (props.displayMode === 'default' || props.displayMode === 'search_verbose'),
                'z-10': userFocusedOn == null && props.displayMode === 'default',
                'z-90': userFocusedOn !== null && props.displayMode === 'default',
                'z-30': props.displayMode === 'search',
                'z-30': userFocusedOn == null && props.displayMode === 'search_verbose',
                'z-20': (userFocusedOn == 'live-in' || userFocusedOn == 'live-out') && props.displayMode === 'search_verbose',
            }
        ]"
        :style="`grid-row: ${util.schedule.start(block.start_time)} / span ${util.schedule.span(block.duration)}`"
    >
        <VDropdown
            :triggers="['hover']"
            :theme="popperTheme()"
            no-auto-focus
            placement="top"
            :disabled="props.displayMode === 'search' || props.displayMode === 'default' && ['lg', 'xl', '2xl'].includes(util.schedule.sizeOf(block)) || props.displayMode === 'search_verbose' && ['lg', 'xl', '2xl'].includes(util.schedule.sizeOf(block)) || (!isCurrentOrFutureWeek && !['3xs', '2xs', 'xs', 'sm'].includes(util.schedule.sizeOf(block)))"
            @apply-hide="handleBlur"
        >
            <ClientCaregiverScheduleViewBlock
                :isCurrentOrFutureWeek="isCurrentOrFutureWeek"
                :block="block"
                :size="util.schedule.sizeOf(block)"
                :displayMode="props.displayMode"
                :showLabel="props.showLabel"
                :class="[
                    'group absolute flex inset-0.5 flex-col overflow-y-auto text-xs whitespace-nowrap cursor-default backdrop-blur-md opacity-90 hover:opacity-100',
                    {
                        'bg-opacity-[0.6] bg-lifeworx-blue-150 border-lifeworx-blue-600 text-lifeworx-blue-600': isCurrentOrFutureWeek && (props.displayMode === 'default' || props.displayMode === 'search_verbose'),
                        'bg-opacity-[0.3] bg-lifeworx-blue-150 border-lifeworx-blue-600 text-lifeworx-blue-500': isCurrentOrFutureWeek && props.displayMode === 'search',
                        'border-lifeworx-blue-900/30 text-lifeworx-blue-900 p-2 bg-gray-300/60': !isCurrentOrFutureWeek,
                        'rounded-t-lg': block.continues,
                        'rounded-b-lg': block.continuation,
                        'rounded-lg': !block.continues && !block.continuation,
                        'p-0.5 border-2': ['3xs', '2xs', 'xs'].includes(util.schedule.sizeOf(block)),
                        'p-1 border-2': ['sm', 'md', 'lg', 'xl', '2xl'].includes(util.schedule.sizeOf(block)),
                        'border-opacity-50 text-opacity-50 opacity-50 [&_*]:text-opacity-50': userFocusedOn !== null && userFocusedOn !== 'client_shift' && (props.displayMode === 'default' || props.displayMode === 'search_verbose'),
                        'border-opacity-50 text-opacity-100 !opacity-75 [&_*]:text-opacity-50': props.displayMode === 'search',
                        'border-opacity-50 text-opacity-100 !opacity-75 [&_*]:text-opacity-50': props.displayMode === 'search_verbose' && userFocusedOn !== 'client_shift',
                        'border-opacity-90 text-opacity-100 opacity-100 [&_*]:text-opacity-100 bg-gray-300/80': userFocusedOn === 'client_shift' && props.displayMode === 'search_verbose' && !isCurrentOrFutureWeek,

                    }
                ]"
            />
            <template #popper>
                <ClientCaregiverScheduleViewBlock
                    class="px-3 pt-3 pb-8"
                    :isCurrentOrFutureWeek="isCurrentOrFutureWeek"
                    :block="block"
                    :size="'2xl'"
                />
            </template>
        </VDropdown>
    </li>
</template>
